import * as React from "react";
import PropTypes from "prop-types";
import cs from "classnames";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { publicConfig } from "@lululemon/ecom-common/src/util/public-config";
import { graphClient } from "@lululemon/mwa-utils";
import * as headerActions from "@lululemon/ecom-common/src/store/actions/mega-nav";
import { trackBraQuizInteraction, updatePageId } from "../../lib/analytics";
import { useDeviceType } from "@lululemon/ecom-site/src/util/with-device-characteristics";
import { braQuizLogger } from "@lululemon/ecom-common/src/util/bra-quiz-logger";
import QuizResults from "./quiz-results/quiz-results";
import QuizQuestion from "./quiz-question/quiz-question";
import QuizButton, { QuizDrawerButtons } from "./quiz-button/quiz-button";
import QuizPagination from "./quiz-pagination/quiz-pagination";
import BraQuizDataContext from "./utils/quizDataContext";
import {
  getFlattedContextData,
  removeBraQuizResultsFromLocalStorage,
  saveBraQuizResultsToLocalStorage,
  formatRequestBody,
  trackBQError,
} from "./utils";
import { findYourBraSizeQuery } from "../../graphql/queries";

import styles from "./quiz.module.scss";

const Quiz = ({
  isEmbedded,
  onCompleteQuiz: handleCompleteQuiz,
  onPaginationChange,
  onNoResults,
}) => {
  const { deviceType } = useDeviceType();
  const isTabletOrBelow = deviceType?.isMobile || deviceType?.isTablet;

  const {
    questions,
    backButtonText,
    nextButtonText,
    seeMyResultsButtonText,
    braQuizResultsAnalyticsId,
  } = getFlattedContextData(React.useContext(BraQuizDataContext));

  const [activeQuestionIndex, setActiveQuestionIndex] = React.useState(0);
  const [answers, setAnswers] = React.useState({
    brands: null,
    bandSizes: null,
    cupSizes: null,
    cupFit2: null,
    dressSizes: null,
    hookClasp: null,
  });
  const [analyticsValues, setAnalyticsValues] = React.useState({
    brands: null,
    bandSizes: null,
    cupSizes: null,
    cupFit2: null,
    dressSizes: null,
    hookClasp: null,
  });
  const [showQuizResults, setShowQuizResults] = React.useState(false);
  const [isFirstStepVisited, setIsFirstStepVisited] = React.useState(false);
  const [isMakingNetworkReq, setIsMakingNetworkReq] = React.useState(false);
  const [isQuestionChanging, setIsQuestionChanging] = React.useState(false);
  const [quizResultsRes, setQuizResultsRes] = React.useState(null);
  const [showNextStepButton, setShowNextStepButton] = React.useState(false);
  const [showFieldValidationError, setFieldValidationError] =
    React.useState(false);
  const [validationErrorTrackingData, setValidationErrorTrackingData] =
    React.useState([]);

  const currentQuestion = activeQuestionIndex;
  const { fields, analyticsId } = questions[currentQuestion];

  const input = [];
  const quizId = "quizSlider";
  const graphqlClient = graphClient();

  React.useEffect(() => {
    if (isEmbedded) {
      onPaginationChange?.(0);
      onNoResults?.(false);
    }

    updatePageId(questions[0].analyticsId);
  }, []);

  function handleKeyDown(event) {
    if (
      event.key === "Enter" &&
      event.target.nodeName !== "BUTTON" &&
      event.target.nodeName !== "A" &&
      !isEmbedded &&
      showNextStepButton
    ) {
      handleNexButtonClick();
    }
  }

  const isLastQuestion = () => activeQuestionIndex + 1 === questions.length;

  const stickyButtonText = isLastQuestion()
    ? seeMyResultsButtonText
    : nextButtonText;

  const fetchQuizResults = async () => {
    setIsMakingNetworkReq(true);
    try {
      const graphOpts = {
        query: findYourBraSizeQuery,
        variables: formatRequestBody(questions, answers),
        url: `${publicConfig.apiHost}/cne/graphql`,
      };
      const quizResultsRes = await graphqlClient(graphOpts);
      const data = quizResultsRes?.data?.findYourBraSize;

      if (data?.sizeSupported) {
        const { pathname = "/" } = new URL(data.cdpURL);
        saveBraQuizResultsToLocalStorage({
          ...data.sizes,
          cdpURL: pathname,
        });
        if (!isEmbedded) {
          trackBraQuizInteraction("results", null, null, null, data.sizes);
        }
      } else {
        if (isEmbedded) {
          onNoResults?.(true);
        }
        removeBraQuizResultsFromLocalStorage();
      }
      setQuizResultsRes(data);
      setIsMakingNetworkReq(false);
    } catch (error) {
      braQuizLogger({
        error,
        componentName: "Quiz",
        functionName: "fetchQuizResults",
        scopeContexts: {
          Answers: answers,
        },
        tags: {
          isEmbedded,
        },
      });
    }
  };

  const handleAnswerReady = (isReady) => {
    setShowNextStepButton(isReady);
  };

  const handleInputFocus = () => {
    input?.[`${activeQuestionIndex}`]?.focus();
  };

  const resetScrollY = () => {
    if (window && window.scrollY !== 0) {
      window.scrollTo(0, 0);
    }
  };

  const updateAnalyticsTracking = (trackEvent) => {
    const nextQuizStep = isLastQuestion()
      ? braQuizResultsAnalyticsId
      : questions[activeQuestionIndex + 1]?.analyticsId;

    const quizStepSelectedValue = fields.map((field) => analyticsValues[field]);

    trackBraQuizInteraction(
      trackEvent,
      nextQuizStep,
      analyticsId,
      quizStepSelectedValue
    );
  };

  const handleNexButtonClick = () => {
    if (!isEmbedded) {
      const isReadyForNextQuestion = showNextStepButton;
      setFieldValidationError(!isReadyForNextQuestion);

      if (!isReadyForNextQuestion) {
        validationErrorTrackingData.forEach((trackingData) =>
          trackBQError(trackingData)
        );
        return;
      }
    }

    resetScrollY();

    if (isLastQuestion()) {
      fetchQuizResults();
      setShowQuizResults(true);
      !isEmbedded && updateAnalyticsTracking("complete");
    } else {
      !isEmbedded && updateAnalyticsTracking("next");
      setIsFirstStepVisited(true);
      setIsQuestionChanging(true);
      setShowNextStepButton(false);
      setTimeout(() => {
        const newActiveQuestionIndex = activeQuestionIndex + 1;
        setActiveQuestionIndex(newActiveQuestionIndex);
        setIsQuestionChanging(false);
        if (isEmbedded) {
          onPaginationChange?.(newActiveQuestionIndex);
        }
      }, 500);
    }
  };

  const handleBackButtonClick = (event) => {
    if (activeQuestionIndex <= 0) return;
    resetScrollY();
    const newActiveQuestionIndex = activeQuestionIndex - 1;
    const { analyticsId: newPageAnalyticsId } =
      questions[newActiveQuestionIndex];
    updatePageId(newPageAnalyticsId);
    setActiveQuestionIndex(newActiveQuestionIndex);
    if (isEmbedded) {
      onPaginationChange?.(newActiveQuestionIndex);
    }
  };

  const handleSaveAnswers = (value, label, analyticsValue) => {
    !isEmbedded && !showNextStepButton && setFieldValidationError(false);
    setAnswers((oldAnswers) => ({ ...oldAnswers, [label]: value }));
    setAnalyticsValues({ ...analyticsValues, [label]: analyticsValue });
  };

  const handleResetQuiz = () => {
    setActiveQuestionIndex(0);
    if (isEmbedded) {
      onPaginationChange?.(0);
      onNoResults?.(false);
    }
    setAnswers({
      brands: null,
      bandSizes: null,
      cupSizes: null,
      cupFit2: null,
      dressSizes: null,
      hookClasp: null,
    });
    setAnalyticsValues({
      brands: null,
      bandSizes: null,
      cupSizes: null,
      cupFit2: null,
      dressSizes: null,
      hookClasp: null,
    });
    setShowQuizResults(false);
    setIsFirstStepVisited(false);
    setIsMakingNetworkReq(false);
    setIsQuestionChanging(false);
    setQuizResultsRes(null);
    setShowNextStepButton(false);
  };

  return (
    <section
      data-testid="bra-quiz-section"
      className={cs(
        "wrapper",
        "container-fluid",
        styles.quiz,
        styles.quizSpaceTop,
        {
          [styles.quizEmbedded]: isEmbedded,
        }
      )}
      id={quizId}
    >
      {showQuizResults ? (
        <QuizResults
          {...{
            isLoading: isMakingNetworkReq,
            quizResultsRes,
            onResetQuiz: handleResetQuiz,
            isEmbedded,
            onCompleteQuiz: handleCompleteQuiz,
          }}
        />
      ) : (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <div
          className={cs(styles.quizContainer, {
            [styles.quizContainerHidden]: isQuestionChanging,
          })}
          onKeyDown={handleKeyDown}
        >
          {!isEmbedded && (
            <div>
              <QuizButton
                isBackButton
                isVisible={activeQuestionIndex !== 0}
                text={backButtonText}
                onClick={handleBackButtonClick}
                aria-controls="quizSlider"
                tabindex={activeQuestionIndex === 0 ? "-1" : "0 "}
              />

              {activeQuestionIndex !== 0 && (
                <QuizPagination
                  questionNumber={activeQuestionIndex + 1}
                  isVisible={!isQuestionChanging}
                  autofocus
                />
              )}
            </div>
          )}
          <div className={styles.quizQuestionsWrapper}>
            {questions.map((question, index) => {
              const isActiveQuestion = activeQuestionIndex === index;

              return (
                <div
                  aria-hidden={!isActiveQuestion}
                  className={cs("col-xs-12", styles.quizSlide, {
                    [styles.quizQuestionsWrapperActiveSlide]:
                      isActiveQuestion && !isQuestionChanging,
                    [styles.quizQuestionsWrapperInactiveSlide]:
                      !isActiveQuestion,
                  })}
                  data-testid={`quiz-slide-question-${index}`}
                  key={question.title}
                >
                  <QuizQuestion
                    activeQuestionIndex={activeQuestionIndex}
                    answers={answers}
                    isActiveQuestion={isActiveQuestion}
                    isEmbedded={isEmbedded}
                    isFirstStepVisited={isFirstStepVisited}
                    isQuestionChanging={isQuestionChanging}
                    isTabletOrBelow={isTabletOrBelow}
                    fieldValidationError={showFieldValidationError}
                    onAnswerReady={handleAnswerReady}
                    onSaveAnswers={handleSaveAnswers}
                    onInputFocus={handleInputFocus}
                    questionIndex={index}
                    questionRef={(inputRef) => {
                      input[index] = inputRef;
                    }}
                    setValidationErrorTrackingData={
                      setValidationErrorTrackingData
                    }
                  />
                </div>
              );
            })}
          </div>

          {!isEmbedded ? (
            <QuizButton
              isStickyButton={isTabletOrBelow}
              isVisible
              text={stickyButtonText}
              onClick={handleNexButtonClick}
              aria-controls={quizId}
              tabIndex={showNextStepButton ? "0" : "-1"}
            />
          ) : (
            <QuizDrawerButtons
              aria-controls={quizId}
              isBackButtonVisible={activeQuestionIndex != 0}
              isLastQuestion={isLastQuestion()}
              activeQuestionIndex={activeQuestionIndex}
              isNextButtonActive={showNextStepButton}
              onNextButtonClick={handleNexButtonClick}
              onBackButtonClick={handleBackButtonClick}
            />
          )}
        </div>
      )}
    </section>
  );
};

Quiz.defaultProps = {
  isEmbedded: false,
  onCompleteQuiz: null,
  onPaginationChange: null,
  onNoResults: null,
};

Quiz.propTypes = {
  isEmbedded: PropTypes.bool,
  onCompleteQuiz: PropTypes.func,
  onPaginationChange: PropTypes.func,
  onNoResults: PropTypes.func,
};

const mapStateToProps = () => ({});
const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      ...headerActions,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(Quiz);
